import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, pipe, tap } from 'rxjs';
import {
  AsyncState,
  asyncError,
  asyncIdle,
  asyncLoading,
  CompetitorStation,
  IsochroneBand,
  MappedSiteInsights,
  ParkingArea,
  ZoningArea,
} from '../models/site-insights.model';
import { SiteInsightsApiRepository } from './site-insights.api.repository';

/** The bundle fetch itself, distinct from a per-layer state. */
type TransportStatus = 'idle' | 'loading' | 'success' | 'error';

/** Null until a layer has a computed-at. */
type ComputedAt = {
  isochrones: string | null;
  competition: string | null;
  parking: string | null;
  zoning: string | null;
};

/** Kept apart from the transport status so a failed reload never discards already-good data. */
type ReloadStatus = 'idle' | 'loading' | 'error';

type SiteInsightsState = {
  /** The site whose bundle is currently held; null before the first load. */
  currentSiteId: string | null;
  /** Drives the single page-level error surface. */
  transportStatus: TransportStatus;
  /** `error` means the last reload failed with the prior data still shown. */
  reloadStatus: ReloadStatus;
  isochrones: AsyncState<IsochroneBand[]>;
  competition: AsyncState<CompetitorStation[]>;
  parking: AsyncState<ParkingArea[]>;
  zoning: AsyncState<ZoningArea[]>;
  computedAt: ComputedAt;
};

const initialState: SiteInsightsState = {
  currentSiteId: null,
  transportStatus: 'idle',
  reloadStatus: 'idle',
  isochrones: asyncIdle(),
  competition: asyncIdle(),
  parking: asyncIdle(),
  zoning: asyncIdle(),
  computedAt: {
    isochrones: null,
    competition: null,
    parking: null,
    zoning: null,
  },
};

/** The per-layer error state a transport failure folds every slice to. */
const TRANSPORT_ERROR_KEY = 'discover.detail.insights.transportError';

/**
 * Four INDEPENDENT layer slices, so a pending or failed layer never blocks the others. The
 * backend→frontend status mapping happens at the repository boundary alongside Zod parsing;
 * the store patches the mapped slices directly.
 *
 * A transport-level failure (network / 5xx / 403) folds all four slices to `error`, so the page
 * shows one banner instead of four per-layer errors. A response for a site the store has moved
 * off is ignored.
 */
export const SiteInsightsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const repo = inject(SiteInsightsApiRepository);

    const patchLoading = (): void =>
      patchState(store, {
        transportStatus: 'loading',
        // Clear a prior site's reload notice: a reload left mid-flight by a site switch would
        // otherwise strand the reload button disabled on the new site.
        reloadStatus: 'idle',
        isochrones: asyncLoading(),
        competition: asyncLoading(),
        parking: asyncLoading(),
        zoning: asyncLoading(),
      });

    const patchSuccess = (mapped: MappedSiteInsights): void =>
      patchState(store, {
        transportStatus: 'success',
        // A successful (re)load clears any prior reload-error notice.
        reloadStatus: 'idle',
        isochrones: mapped.isochrones,
        competition: mapped.competition,
        parking: mapped.parking,
        zoning: mapped.zoning,
        computedAt: mapped.computedAt,
      });

    const patchTransportError = (): void =>
      patchState(store, {
        transportStatus: 'error',
        isochrones: asyncError(TRANSPORT_ERROR_KEY),
        competition: asyncError(TRANSPORT_ERROR_KEY),
        parking: asyncError(TRANSPORT_ERROR_KEY),
        zoning: asyncError(TRANSPORT_ERROR_KEY),
      });

    const readOne = rxMethod<string>(
      pipe(
        tap(() => patchLoading()),
        concatMap((siteId) =>
          repo.loadForSite(siteId).pipe(
            tapResponse({
              // Ignore a response for a site the store has moved off of.
              next: (mapped) => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchSuccess(mapped);
              },
              error: () => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchTransportError();
              },
            }),
          ),
        ),
      ),
    );

    // Unlike the initial load this neither folds the slices to loading up front nor to error
    // on failure: the prior data stays on screen behind a transient notice, so a failed
    // re-check never discards already-computed layers.
    const reloadOne = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { reloadStatus: 'loading' })),
        concatMap((siteId) =>
          repo.loadForSite(siteId).pipe(
            tapResponse({
              next: (mapped) => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchSuccess(mapped);
              },
              error: () => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchState(store, { reloadStatus: 'error' });
              },
            }),
          ),
        ),
      ),
    );

    return {
      /** Re-entering the already-current site is a no-op, so returning to the tab does not
       * refetch. */
      loadForSite(siteId: string): void {
        if (store.currentSiteId() === siteId) {
          return;
        }
        patchState(store, { currentSiteId: siteId });
        readOne(siteId);
      },
      /**
       * Bypasses the same-site guard, so it always refetches: the one way to pull
       * newly-computed layers and to recover from a transport error. No-op before any load.
       */
      reloadCurrentSite(): void {
        const siteId = store.currentSiteId();
        if (siteId == null) {
          return;
        }
        reloadOne(siteId);
      },
    };
  }),
);
